import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import _ from 'underscore';

import LanguageService from 'packages/kakapo-ui-services/services/common/LanguageService';
import DropdownList, {
	DropdownListItem,
} from 'packages/kakapo-ui-kit/components/lists/DropdownList';
import { HorizontalAlignment } from 'packages/kakapo-ui-kit/types/Layout';
import Spinner from '../../common/Spinner';
import useSupportedLanguageIDs from 'packages/kakapo-ui-application/hooks/useSupportedLanguageIDs';

const LanguageSelectorContainer = styled.div`
	display: inline-block;
	position: relative;
`;

const LanguageSelectorButton = styled.a`
	color: ${(props) => props.theme.colors.slateBlue};
	border-radius: 6px;
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	padding: 5px 11px;
	display: inline-block;
	text-decoration: none;

	&:hover {
		color: ${(props) => props.theme.colors.antracite};
	}

	img {
		vertical-align: middle;
		display: inline-block;
		margin-right: 8px;
		margin-left: -3px;
		margin-top: -2px;
		width: 20px;
	}
`;

interface IProps {
	dropdownAlignment?: HorizontalAlignment;
	onLanguageChange?: (languageId: 'nl' | 'en') => void;
}

export default function LanguageSelector({ dropdownAlignment, onLanguageChange }: IProps) {
	const { t, i18n } = useTranslation();

	const service = new LanguageService();
	const [currentLanguage, setCurrentLanguage] = useState(service.currentLanguage);
	const [isDropdownHidden, setIsDropdownHidden] = useState(true);

	const supportedLanguageIDs = useSupportedLanguageIDs();

	useEffect(() => {
		// Only change language when an option has been clicked (which means dropdown is open)
		if (!isDropdownHidden) {
			service.currentLanguage = currentLanguage;
			i18n.changeLanguage(currentLanguage);
			if (onLanguageChange !== undefined) {
				onLanguageChange(currentLanguage as 'nl' | 'en');
			}
		}
		setIsDropdownHidden(true);
	}, [currentLanguage]);

	if (!supportedLanguageIDs) {
		return <Spinner isLoading size="18px" verticalMargin={0} />;
	}

	const languageNames = _.pick(
		service.languageNames(),
		...supportedLanguageIDs.map((id) => id.split('_')[0]),
	);

	return (
		<LanguageSelectorContainer>
			<LanguageSelectorButton
				href="#"
				onClick={(event) => {
					event.preventDefault();
					setIsDropdownHidden(!isDropdownHidden);
				}}
			>
				<img
					width="20"
					height="14"
					src={`/images/languages/${currentLanguage}.svg`}
					alt={currentLanguage}
				/>
				{t('common.language.changeLanguage')}
			</LanguageSelectorButton>

			{isDropdownHidden !== true && (
				<DropdownList horizontalAlignment={dropdownAlignment}>
					{Object.keys(languageNames).map((language) => (
						<DropdownListItem key={`LanguageSelectorDropdownItem-${language}`}>
							{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
							<a
								href="#"
								onClick={(event) => {
									event.preventDefault();
									setCurrentLanguage(language);
								}}
							>
								<img
									width="20"
									height="14"
									src={`/images/languages/${language}.svg`}
									alt={language}
								/>
								{languageNames[language]}
							</a>
						</DropdownListItem>
					))}
				</DropdownList>
			)}
		</LanguageSelectorContainer>
	);
}
